import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Zap, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Snowflake', icon: Database },
  { name: 'Apache Airflow', icon: Cloud },
  { name: 'PostgreSQL', icon: Database },
  { name: 'Salesforce', icon: Server },
  { name: 'NPI Lookup API', icon: Shield }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '100%',
    label: 'Client Automation',
    description: 'Across 15+ pharmaceutical marketing clients'
  },
  {
    icon: CheckCircle2,
    value: '95%+',
    label: 'Data Accuracy',
    description: 'Through automated NPI-based provider validation'
  },
  {
    icon: Zap,
    value: '70%',
    label: 'Cost Reduction',
    description: 'In operational overhead across all client deployments'
  },
  {
    icon: Clock,
    value: '80%',
    label: 'Faster Onboarding',
    description: 'Through standardized implementation templates'
  }
];

const highlights = [
  {
    category: 'Automation Excellence',
    items: [
      'Achieved 100% automation of data refresh and physician matching across all 15+ pharmaceutical marketing clients',
      'Reduced client onboarding time by 80% through metadata-driven configuration replacing custom code deployments',
      'Eliminated all manual NPI validation with real-time healthcare provider lookup integrated directly into pipelines'
    ]
  },
  {
    category: 'Operational Efficiency',
    items: [
      'Reduced operational costs by 70% through automated workflows replacing manual reporting dependencies',
      'Maintained 95%+ data accuracy across multi-source integrations from PostgreSQL and Salesforce',
      'Delivered compliance-ready PLD reporting aligned with NPI-based physician-level data validation standards'
    ]
  }
];

const challenges = [
  {
    title: 'Inconsistent Provider Matching Across 15+ Clients',
    icon: Database,
    problem: 'Each client operated with different source systems and NPI data formats, making standardized physician matching impossible without manual intervention per deployment.',
    solution: 'Built a centralized Physician Matching Engine with automated NPI lookup and normalization, handling multi-format inputs and producing standardized PLD-compliant output regardless of source.',
    impact: 'Consistent 95%+ match accuracy across all client deployments from a single shared engine'
  },
  {
    title: 'Manual Reporting Dependencies Blocking Compliance',
    icon: FileText,
    problem: 'Reports were generated manually per client, causing delays, inconsistencies, and compliance risk during regulatory reporting windows when accuracy and timeliness were critical.',
    solution: 'Implemented Apache Airflow DAGs to automate end-to-end report generation, scheduling, and delivery with built-in validation gates ensuring data quality before any output was produced.',
    impact: '100% automated reporting across all clients with zero manual touchpoints'
  },
  {
    title: 'Scalability Ceiling from Legacy Infrastructure',
    icon: Cloud,
    problem: 'Adding a new pharmaceutical client required weeks of custom pipeline configuration, matching rule setup, and report template engineering — making growth operationally unsustainable.',
    solution: 'Designed a metadata-driven Snowflake architecture where new clients are fully onboarded through configuration parameters, with no changes to underlying pipeline code required.',
    impact: '80% faster client onboarding with a validated, repeatable deployment framework'
  },
  {
    title: 'Fragmented Multi-Source Data Quality',
    icon: Shield,
    problem: 'Disconnected PostgreSQL and Salesforce sources produced inconsistent data formats and values, creating systematic quality issues that surfaced only at reporting time — too late to correct cleanly.',
    solution: 'Built automated quality frameworks with validation applied at every pipeline stage, ensuring data integrity was enforced at ingestion before any downstream processing or reporting occurred.',
    impact: '95%+ data accuracy maintained across all sources with full audit trail for compliance review'
  }
];

const PharmaAnalytics = () => {
  return (
    <>
      <SEOHead
        title="Pharmaceutical Marketing Analytics Platform Case Study | Agility AI"
        description="How Agility built a Snowflake-powered platform automating NPI-based physician matching and compliance reporting across 15+ pharmaceutical marketing clients — 100% automation, 95%+ accuracy, 70% cost reduction."
        url="https://www.agilitytech.ai/case-studies/pharma-analytics"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Pharmaceutical Marketing Analytics Platform",
          "description": "How Agility built a Snowflake-powered platform automating NPI-based physician matching and compliance reporting across 15+ pharmaceutical marketing clients.",
          "author": { "@type": "Organization", "name": "Agility" },
          "publisher": {
            "@type": "Organization",
            "name": "Agility",
            "logo": { "@type": "ImageObject", "url": "https://www.agilitytech.ai/agility hex fonts.png" }
          },
          "datePublished": "2025-10-01",
          "dateModified": "2026-05-01",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.agilitytech.ai/case-studies/pharma-analytics" }
        }}
      />
      <div className="pt-20 bg-black">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?auto=format&fit=crop&q=80"
              alt="Pharmaceutical Analytics Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Pharmaceutical Marketing</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Pharmaceutical Marketing Analytics Platform
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
                Automating physician-level data validation and multi-client reporting for a fragmented pharmaceutical marketing infrastructure serving 15+ clients through a scalable, compliance-ready Snowflake platform.
              </p>
            </div>
          </div>
        </section>

        {/* Key Metrics Section */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
                <p className="text-white">Pharmaceutical Marketing</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
                <p className="text-white">USA</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
                <p className="text-white">15+ Client Deployments</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
                <p className="text-white">Q2 2024 - Completed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Fragmented Pharmaceutical Reporting Infrastructure</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      A pharmaceutical marketing firm managing data operations for 15+ clients was running on legacy reporting infrastructure held together by manual dependencies and inconsistent provider matching logic. NPI-based physician-level data validation was done manually per client, compliance reporting was error-prone, and adding a new client required weeks of custom engineering. Fragmented systems across PostgreSQL and Salesforce sources meant no single version of truth, and scalability was structurally impossible without a complete rebuild.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Scalable Compliance-Ready Platform</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Scalable Compliance-Ready Platform</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      We replaced the fragmented, manually-operated reporting infrastructure with a fully automated, metadata-driven Snowflake platform that handles all 15+ pharmaceutical marketing clients from a single shared architecture. By automating NPI-based physician matching, orchestrating pipelines through Apache Airflow, and designing for configuration-driven client onboarding, we delivered a system where adding a new client takes days instead of weeks — with 100% automation and 95%+ data accuracy across every deployment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8
                  border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                        Problem
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                        Solution
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>

                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Database className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Automate Your Data Operations?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Contact our data engineering specialists to discover how a metadata-driven platform can transform your multi-client reporting operations.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4
                  rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PharmaAnalytics;
